import EChartCommon from '@/pages/opinion/components/EChartCommon';
import React, { useMemo } from 'react';
import { echartsContent, getPercentageInfo } from '@/pages/opinion/utils';
import { useIntl } from 'umi';
import {
  echartTooltipTime,
  formatChartParamsDate,
} from '@/pages/opinion/components/ChartCommon/formatter';
import { getIconInfo } from './utils';
import { reviewsFormat, sentimentFormat } from '@/pages/opinion/utils/value';
import useGameChartRich, { xArr } from '../../hooks/useGameChartRich';

export default function BarAndLineChart({
  xAxisList,
  data,
  height,
  commonParams,
}: any) {
  const { xRich, gameInfo, isSingle } = useGameChartRich({ xAxisList });

  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    const chartValue: any = {
      Sentiment: [],
      Negative: [],
      Neutral: [],
      Positive: [],
      games: [],
    };
    if (data) {
      data.forEach((item: any) => {
        const key = item.name;
        chartValue[key] = item.games.map((info: any) => info.value);
      });
      xAxisList?.map((item: any) => {
        chartValue.games.push(item.edition_unified_id);
      });
    }

    const option = {
      tooltip: {
        formatter: (params: any) => {
          const { axisValue } = params?.[0] || {};
          const { name, cover } = gameInfo[axisValue];
          const sentimentIndex = params.findIndex(
            (item: any) => item.seriesName === 'Sentiment',
          );
          let realParams = [],
            sentimentInfo = {};
          if (sentimentIndex >= 0) {
            realParams = params.filter(
              (_: any, index: number) => index !== sentimentIndex,
            );
            sentimentInfo = params[sentimentIndex];
          }

          return formatChartParamsDate({
            params: realParams,
            title: echartTooltipTime(commonParams),
            beforeSort: false,
            listTitleFunc(item) {
              return formatMessage({ id: item.seriesName });
            },
            dealwithRecords(records, total) {
              const realRecords = records.map((item) => ({
                ...item,
                value:
                  item.value +
                  `(${getPercentageInfo(item.originValue, total, 2)})`,
              }));
              realRecords.unshift(
                ...[
                  {
                    text: formatMessage({ id: 'Mentions' }),
                    value: reviewsFormat(total),
                    originText: 'Mentions',
                    originValue: total,
                  },
                  {
                    text: `<span class="chart-common-formatter-marker">${
                      sentimentInfo?.marker
                    }</span>&nbsp;${formatMessage({ id: 'Sentiment' })}`,
                    value: sentimentFormat(sentimentInfo?.value || 0) as any,
                    originText: 'Sentiment',
                    originValue: sentimentInfo?.value || 0,
                  },
                ],
              );
              return realRecords;
            },
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: getIconInfo(cover, name, undefined, 24, 12),
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: '',
                  },
                ],
              },
            ],
          });
        },
      },
      grid: {
        bottom: '30px',
        top: '5%',
        containLabel: true,
      },
      legend: {
        data: ['Sentiment', 'Positive', 'Neutral', 'Negative'],
        formatter: (value: string) => {
          return formatMessage({ id: value });
        },
      },
      xAxis: {
        boundaryGap: true,
        data: chartValue.games,
        axisLabel: {
          interval: 'auto',
          formatter: function (_value: any, index: number) {
            return '{' + xArr[index] + '| }';
          },
          rich: {
            value: {
              lineHeight: 30,
              align: 'center',
              width: 300,
              height: 300,
            },
            ...xRich,
          },
        },
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            color: '#838697',
          },
          nameTextStyle: {
            color: '#838697',
            fontWeight: 400,
            fontSize: '12px',
          },
          splitNumber: 5,
        },
        {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => value.toFixed(0),
            color: '#838697',
            customValues: [0, 1, 2, 3, 4, 5],
          },
          splitLine: { show: false },
          max: 5,
          min: 0,
          splitNumber: 5,
        },
      ],
      series: [
        {
          name: 'Positive',
          type: 'bar',
          stack: '1',
          yAxisIndex: 0,
          data: chartValue.Positive,
          itemStyle: {
            color: '#4FCB87',
          },
        },
        {
          name: 'Neutral',
          type: 'bar',
          stack: '1',
          yAxisIndex: 0,
          data: chartValue.Neutral,
          itemStyle: {
            color: '#7E92A1',
          },
        },
        {
          name: 'Negative',
          type: 'bar',
          stack: '1',
          yAxisIndex: 0,
          data: chartValue.Negative,
          itemStyle: {
            color: '#E95F6A',
          },
        },
        {
          name: 'Sentiment',
          type: 'line',
          yAxisIndex: 1,
          lineStyle: {
            color: '#5086F3',
          },
          data: chartValue.Sentiment,
        },
      ],
    };
    console.log(options, '------------');
    return option;
  }, [data, xRich, formatMessage]);

  return (
    <>
      <EChartCommon
        optionsProps={{ excludeOptions: ['axisPointer'], type: 'bar' }}
        height={height}
        leftTitle={formatMessage({ id: 'Mentions' })}
        rightTitle={formatMessage({ id: 'Sentiment' })}
        options={options}
      ></EChartCommon>
    </>
  );
}
